import React, { useState, useEffect } from "react";

//#region Material Components
import { withStyles } from "@material-ui/styles";
import MuiDialog from "@material-ui/core/Dialog";
import { CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CropIcon from "@material-ui/icons/Crop";
import ShareIcon from "@material-ui/icons/Share";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
//#endregion

//#region Internal
import { getImageUrlR, cropPage, updateCropAnswer } from "../../api/FeedBacks";
import { getCMSReportDetails } from "../../api/CMSReports";
import { getEditorialSystemId } from "../../api/BookSectionCrop";
import EditorialSystemId from "../../enums/EditorialSystemId";
import { FB, fmtDate } from "../../assets/feedbackTheme";
import { toast } from "react-toastify";
//#endregion

// CMS Reports "İncele" modal. Shares the Feedbacks edit-modal LOOK (FB theme,
// header/status, env image frame, answer pills, reports list) but keeps the CMS
// Reports page's own REQUESTS: cropPage (report/cropPage), getCMSReportDetails
// (report/cmsdetails) and the DEV-only cropped image (these questions aren't in
// PROD). Answer change hits DEV only (updateCropAnswer) for the same reason.
// Mark-done / delete stay as row-level actions on the CMS Reports page.

const Dialog = withStyles({
  paper: { borderRadius: 12 },
  paperWidthSm: { maxWidth: 960, minWidth: 320 },
})(MuiDialog);

const baseBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  border: `1px solid ${FB.borderStrong}`,
  background: FB.surface,
  color: FB.text,
};

const primaryBtn = {
  ...baseBtn,
  background: FB.doneSoft,
  color: FB.done,
  border: `1px solid ${FB.doneBorder}`,
};

const ansPill = (isCorrect, isPending) => ({
  width: 42,
  height: 42,
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: isCorrect ? FB.accentSoft : FB.surface,
  color: isCorrect ? FB.accent : FB.text,
  border: isPending
    ? `2px solid ${FB.accent}`
    : `1px solid ${isCorrect ? FB.accentBorder : FB.borderStrong}`,
});

function CMSReportEditDialog(props) {
  const { user, isOpen, onClose, selectedCropId, isDeleted } = props;

  const [details, setDetails] = useState([]);
  const [cropIdDetails, setCropIdDetails] = useState(null);
  const [imageUrl, setImageUrl] = useState({
    mainImage: null,
    imageParts: [],
    RowStatus: false,
  });
  const [editorialSystemIdInfo, setEditorialSystemIdInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Answer change (DEV only). answerOverride optimistically reflects the just-set
  // answer until the modal is reopened for another question.
  const [showConfirm, setShowConfirm] = useState(false);
  const [answerSubmitting, setAnswerSubmitting] = useState(false);
  const [answerOverride, setAnswerOverride] = useState(null);
  const [pendingOption, setPendingOption] = useState("");

  const baseAnswer = cropIdDetails ? cropIdDetails.AnswerOption : "";
  const correct = answerOverride != null ? answerOverride : baseAnswer || "";

  // ─── Loaders (unchanged CMS requests) ────────────────────────────────────────

  const getCropDetails = async () => {
    const res = await cropPage(Number(selectedCropId), user.Token);
    if (res.status === 200) {
      setCropIdDetails(res.data);
    } else {
      onClose();
    }
  };

  const getReportRows = async () => {
    const res = await getCMSReportDetails(selectedCropId, user.Token);
    if (res.status === 200) {
      setDetails(
        Array.isArray(res.data?.ResponseData) ? res.data.ResponseData : []
      );
    }
  };

  const fetchImageUrl = async () => {
    if (!cropIdDetails) return;
    const param = {
      BookSectionId: cropIdDetails.BookSectionId,
      QuestionNumber: cropIdDetails.QuestionNumber,
    };
    try {
      // These questions don't exist in PROD, so we only fetch the DEV image.
      const devRes = await getImageUrlR(param, user.Token);
      if (devRes && devRes.status === 200) {
        const { ImageUrl, ImagePartUrls, RowStatus } = devRes.data;
        setImageUrl({
          mainImage: ImageUrl,
          imageParts: ImagePartUrls || [],
          RowStatus: RowStatus,
        });
      }
    } catch (error) {
      console.error("CMSReportEditDialog fetchImageUrl error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEditorialSystemId = async () => {
    if (!cropIdDetails) return;
    try {
      const res = await getEditorialSystemId(
        cropIdDetails.BookSectionId,
        user.Token
      );
      if (res.status === 200) {
        setEditorialSystemIdInfo(res.data.ResponseData);
      }
    } catch (error) {
      console.error("CMSReportEditDialog fetchEditorialSystemId error:", error);
    }
  };

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !selectedCropId) return;
    setCropIdDetails(null);
    setDetails([]);
    setImageUrl({ mainImage: null, imageParts: [], RowStatus: false });
    setEditorialSystemIdInfo(null);
    setShowConfirm(false);
    setAnswerSubmitting(false);
    setAnswerOverride(null);
    setPendingOption("");
    setIsLoading(true);
    getCropDetails();
    getReportRows();
  }, [isOpen, selectedCropId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !cropIdDetails) return;
    fetchImageUrl();
    fetchEditorialSystemId();
  }, [cropIdDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const shareLink = async () => {
    if (!cropIdDetails) return;
    const param = {
      BookSectionId: cropIdDetails.BookSectionId,
      QuestionNumber: cropIdDetails.QuestionNumber,
    };
    const res = await getImageUrlR(param, user.Token);
    if (res.status === 200) {
      let url = "https://product.pakodemy.com/share/index.htm?q=";
      let q = res.data.ImageUrl.substring(res.data.ImageUrl.indexOf("crops") + 6);
      const part = res.data.ImagePartUrls && res.data.ImagePartUrls[0];
      if (part) q += `__${part.substring(part.indexOf("crops") + 6)}`;
      url += q;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) w.opener = null;
    }
  };

  const openVideo = () => {
    if (!cropIdDetails || !cropIdDetails.AnswerVideoPath) return;
    const w = window.open(
      cropIdDetails.AnswerVideoPath,
      "_blank",
      "noopener,noreferrer"
    );
    if (w) w.opener = null;
  };

  const goToCropPage = () => {
    if (!cropIdDetails) return;
    localStorage.setItem("bookSectionId", cropIdDetails.BookSectionId);
    localStorage.setItem("targetQuestion", cropIdDetails.QuestionNumber);
    sessionStorage.setItem("fromFeedBack", true);
    const w = window.open("/booksectioncrop", "_blank");
    if (w) w.opener = null;
  };

  // Answer change — DEV only (these questions aren't in PROD, so no updateAnswer).
  const changeAnswer = async (option) => {
    const params = {
      BookSectionCropId: Number(selectedCropId),
      AnswerOption: option,
    };
    const res = await updateCropAnswer(params, user.Token);
    if (res.status === 200) {
      toast.success("Cevap güncellendi.");
      return true;
    }
    toast.error("Cevap güncellenemedi.");
    return false;
  };

  // ─── Derived ─────────────────────────────────────────────────────────────────

  const deleted =
    isDeleted || (!imageUrl.RowStatus && !isLoading && !!cropIdDetails);
  const qNo = cropIdDetails ? cropIdDetails.QuestionNumber : null;
  const sectionId = cropIdDetails ? cropIdDetails.BookSectionId : null;
  const statusVal = cropIdDetails ? cropIdDetails.Status : null;
  const headerSub = [
    qNo != null ? `Soru ${qNo}` : null,
    sectionId != null ? `bookSection #${sectionId}` : null,
    `${details.length} rapor`,
  ]
    .filter(Boolean)
    .join("  ·  ");

  const statusBadge =
    statusVal == null ? null : Number(statusVal) === 0 ? (
      <span
        style={{
          ...styles.statusBadge,
          background: FB.openSoft,
          color: FB.open,
          border: `1px solid ${FB.openBorder}`,
        }}
      >
        Açık
      </span>
    ) : (
      <span
        style={{
          ...styles.statusBadge,
          background: FB.doneSoft,
          color: FB.done,
          border: `1px solid ${FB.doneBorder}`,
        }}
      >
        Çözülen
      </span>
    );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog id={props.id} open={isOpen} onClose={onClose}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>Soru raporu</div>
            {headerSub ? <div style={styles.headerSub}>{headerSub}</div> : null}
          </div>
          <div style={styles.headerRight}>
            {statusBadge}
            <button style={styles.closeBtn} aria-label="Kapat" onClick={onClose}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {isLoading ? (
            <div style={styles.center}>
              <CircularProgress style={{ color: FB.accent }} />
            </div>
          ) : (
            <div style={styles.imageBlock}>
              <div style={styles.envHeaderRow}>
                <span style={{ ...styles.envBadge, backgroundColor: FB.dev }}>
                  Dev
                </span>
                <div style={styles.envMeta}>
                  <span style={styles.metaItem}>
                    <span style={styles.metaKey}>CropId:</span>
                    {selectedCropId != null ? selectedCropId : "-"}
                  </span>
                  <span style={styles.metaItem}>
                    <span style={styles.metaKey}>Cevap:</span>
                    {correct || "-"}
                  </span>
                  {cropIdDetails && cropIdDetails.AnswerVideoPath ? (
                    <PlayCircleFilledIcon
                      titleAccess="Videoyu yeni sekmede aç"
                      onClick={openVideo}
                      style={styles.videoIcon}
                    />
                  ) : null}
                </div>
              </div>
              <div style={styles.imageFrame}>
                {imageUrl.mainImage ? (
                  <>
                    <img
                      src={imageUrl.mainImage}
                      alt="Soru"
                      style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                    />
                    {imageUrl.imageParts.map((partUrl, index) => (
                      <img
                        key={index}
                        src={partUrl}
                        alt={`Parça ${index + 1}`}
                        style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                      />
                    ))}
                  </>
                ) : (
                  <div style={styles.envEmpty}>Görsel bulunamadı</div>
                )}
              </div>
            </div>
          )}

          {/* Answer (change, DEV only) */}
          <div style={styles.sectionCard}>
            <div style={styles.sectionLabel}>
              <span>Doğru cevap</span>
              <span style={{ color: FB.textMuted }}>
                Değiştirmek için bir seçenek seçin
              </span>
            </div>
            <div style={styles.ansRow}>
              {["A", "B", "C", "D", "E"].map((opt) => (
                <button
                  key={opt}
                  disabled={answerSubmitting}
                  onClick={() => {
                    setPendingOption(opt);
                    setShowConfirm(true);
                  }}
                  style={ansPill(correct === opt, showConfirm && pendingOption === opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
            {showConfirm && (
              <div style={styles.confirmBox}>
                <div style={styles.confirmText}>
                  {answerSubmitting
                    ? "Cevap değiştiriliyor…"
                    : "Cevap değiştirmeyi onaylıyor musun?"}
                </div>
                <div style={styles.confirmBtns}>
                  <button
                    style={{
                      ...baseBtn,
                      ...(answerSubmitting ? { opacity: 0.6, cursor: "default" } : {}),
                    }}
                    disabled={answerSubmitting}
                    onClick={() => {
                      setPendingOption("");
                      setShowConfirm(false);
                    }}
                  >
                    İptal
                  </button>
                  <button
                    style={{ ...primaryBtn, minWidth: 96, justifyContent: "center" }}
                    disabled={answerSubmitting}
                    onClick={async () => {
                      const chosen = pendingOption;
                      setAnswerSubmitting(true);
                      try {
                        const ok = await changeAnswer(chosen);
                        if (ok) setAnswerOverride(chosen);
                        setShowConfirm(false);
                        setPendingOption("");
                      } finally {
                        setAnswerSubmitting(false);
                      }
                    }}
                  >
                    {answerSubmitting ? (
                      <CircularProgress size={16} style={{ color: FB.done }} />
                    ) : (
                      "Değiştir"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={styles.toolbar}>
            {editorialSystemIdInfo === EditorialSystemId.CropPage && (
              <button style={baseBtn} onClick={goToCropPage}>
                <CropIcon style={{ fontSize: 18 }} /> Crop sayfası
              </button>
            )}
            <button style={baseBtn} onClick={shareLink}>
              <ShareIcon style={{ fontSize: 18 }} /> Paylaş
            </button>
            {cropIdDetails && cropIdDetails.AnswerVideoPath && (
              <button style={baseBtn} onClick={openVideo}>
                <PlayCircleFilledIcon style={{ fontSize: 18 }} /> Videoyu aç
              </button>
            )}
            {deleted && <span style={styles.deletedNote}>Bu soru silinmiştir</span>}
          </div>

          {/* Reports */}
          <div style={styles.reportsHeader}>
            Kullanıcı raporları{" "}
            <span style={{ color: FB.textMuted, fontWeight: 400 }}>
              · {details.length}
            </span>
          </div>
          {details.length === 0 ? (
            <div style={styles.emptyReports}>Kullanıcı raporu yok.</div>
          ) : (
            <div style={styles.reportsList}>
              {details.map((item, i) => {
                const last = i === details.length - 1;
                return (
                  <div
                    key={item.Id != null ? item.Id : i}
                    style={{
                      ...styles.reportRow,
                      ...(last ? { borderBottom: "none" } : {}),
                    }}
                  >
                    <div style={styles.avatar}>{item.UserId}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.reasonChips}>
                        {Array.isArray(item.IssueTypeNames) &&
                        item.IssueTypeNames.length ? (
                          item.IssueTypeNames.map((name, ni) => (
                            <span key={ni} style={styles.reasonChip}>
                              {name}
                            </span>
                          ))
                        ) : (
                          <span style={{ fontSize: 12, color: FB.textMuted }}>—</span>
                        )}
                      </div>
                      {item.Message ? (
                        <div style={styles.reportComment}>{item.Message}</div>
                      ) : null}
                    </div>
                    <div style={styles.reportDate}>
                      {item.CreatedDate ? fmtDate(item.CreatedDate) : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

const styles = {
  modal: { width: "100%" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: `1px solid ${FB.border}`,
  },
  headerTitle: { fontSize: 17, fontWeight: 600, color: FB.text },
  headerSub: { fontSize: 12, color: FB.textMuted, marginTop: 2 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  statusBadge: {
    fontSize: 12,
    fontWeight: 500,
    padding: "3px 10px",
    borderRadius: 999,
  },
  closeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${FB.border}`,
    background: FB.surface,
    color: FB.textSecondary,
    cursor: "pointer",
  },
  body: { padding: 20, maxHeight: "78vh", overflowY: "auto" },
  center: { display: "flex", justifyContent: "center", padding: 30 },

  imageBlock: { marginBottom: 18 },
  envHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  envBadge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    letterSpacing: 0.4,
  },
  envMeta: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12,
    color: FB.textSecondary,
    flexWrap: "wrap",
  },
  metaItem: { whiteSpace: "nowrap" },
  metaKey: { color: FB.textMuted, marginRight: 4 },
  videoIcon: { cursor: "pointer", color: FB.accent, fontSize: 20 },
  imageFrame: {
    border: `1px solid ${FB.border}`,
    borderRadius: 10,
    background: FB.surfaceAlt,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    minHeight: 120,
  },
  envEmpty: {
    color: FB.textMuted,
    fontSize: 13,
    padding: "28px 8px",
    textAlign: "center",
  },

  sectionCard: {
    border: `1px solid ${FB.border}`,
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: 13,
    color: FB.textSecondary,
    marginBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  ansRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  confirmBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    background: FB.openSoft,
    border: `1px solid ${FB.openBorder}`,
  },
  confirmText: {
    fontSize: 13,
    color: FB.open,
    marginBottom: 10,
    textAlign: "center",
  },
  confirmBtns: { display: "flex", justifyContent: "center", gap: 10 },

  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  deletedNote: { marginLeft: "auto", color: FB.danger, fontSize: 13 },

  reportsHeader: {
    fontSize: 13,
    fontWeight: 600,
    color: FB.text,
    marginBottom: 8,
  },
  reportsList: {
    border: `1px solid ${FB.border}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  reportRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "10px 12px",
    borderBottom: `1px solid ${FB.border}`,
  },
  avatar: {
    height: 26,
    padding: "0 10px",
    borderRadius: 999,
    background: FB.accentSoft,
    color: FB.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  reasonChips: { display: "flex", gap: 6, flexWrap: "wrap" },
  reasonChip: {
    fontSize: 12,
    padding: "2px 8px",
    borderRadius: 999,
    background: FB.accentSoft,
    color: FB.accent,
    border: `1px solid ${FB.accentBorder}`,
  },
  reportComment: { fontSize: 12, color: FB.textSecondary, marginTop: 3 },
  reportDate: {
    fontSize: 12,
    color: FB.textMuted,
    whiteSpace: "nowrap",
    marginLeft: "auto",
  },
  emptyReports: {
    padding: 20,
    textAlign: "center",
    color: FB.textMuted,
    fontSize: 13,
  },
};

export default CMSReportEditDialog;
